import { Deck } from "@/lib/classes/deck";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UnitCategory } from "@/lib/types/deck-builder";
import { Copy, Check } from "lucide-react";
import { useState } from "react";
import { exportDeckToCode } from "@/lib/utils/deck-code";
import { toast } from "sonner";

interface SummaryViewProps {
  deck: Deck;
}

export function SummaryView({ deck }: SummaryViewProps) {
  const [copied, setCopied] = useState(false);
  const totalCost = deck.getSumOfUnitsCosts();
  const usedAP = deck.usedActivationPoints;
  const maxAP = deck.division.maxActivationPoints;
  const remainingAP = maxAP - usedAP;
  
  // Calculate total unit count across all categories (accounting for veterancy quantities)
  const totalUnitCount = deck.unitCategories.reduce((sum, category) => {
    return sum + deck.getTotalUnitCountForCategory(category);
  }, 0);
  
  // Total cards is the number of card slots
  const totalCards = deck.units.length;

  const handleCopyCode = async () => {
    try {
      await exportDeckToCode(deck);
      setCopied(true);
      toast.success("Deck code copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to export deck code:", error);
      toast.error("Failed to copy deck code to clipboard");
    }
  };

  const deckCode = deck.toDeckCode();

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <h3 className="text-lg font-semibold mb-4">Deck Summary</h3>
          
          <div className="space-y-2 mb-4">
            <div className="flex justify-between">
              <span className="text-sm">Activation Points:</span>
              <span className="text-sm font-medium">
                {usedAP} / {maxAP}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Remaining AP:</span>
              <span className="text-sm font-medium">{remainingAP}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Total Command Points:</span>
              <span className="text-sm font-medium">{totalCost}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Total Cards:</span>
              <span className="text-sm font-medium">{totalCards}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Total Units:</span>
              <span className="text-sm font-medium">{totalUnitCount}</span>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold mb-2">Units by Category</h4>
            <div className="space-y-1">
              {deck.unitCategories.map((category) => {
                const count = deck.getTotalUnitCountForCategory(category);
                const cost = deck.getSumOfUnitCostsForCategory(category);
                if (count === 0) return null;

                return (
                  <div key={category} className="flex justify-between text-xs">
                    <span>{category.split("/").pop()}:</span>
                    <span>{count} units ({cost} CP)</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold">Deck Code</h4>
              <Button
                onClick={handleCopyCode}
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={copied}
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <div className="mt-2">
              <Textarea
                value={deckCode}
                readOnly
                className="font-mono text-xs min-h-[80px] resize-none"
                onClick={(e) => (e.target as HTMLTextAreaElement).select()}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

